import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { environment } from '../../environments/environment';
import { AuthenticationService } from '../../shared/services/authentication.service';
import { IPost, IPostUpsert, PostVideoType } from '../model/post.model';

@Injectable()
export class PostService {
    private http = inject(HttpClient);
    private authService = inject(AuthenticationService);

    getPostsByCommunity(communityId: string): Observable<IPost[]> {
        const token = this.authService.getUserToken();
        if (!this.hasValidToken(token) || !communityId) {
            return of([]);
        }

        return this.http.get<IPost[]>(
            `${environment.API_URL}/api/Post/community/${communityId}`,
            this.getAuthOptions(token)
        );
    }

    getPost(id: string): Observable<IPost> {
        const token = this.authService.getUserToken();
        if (!this.hasValidToken(token) || !id) {
            return of();
        }

        return this.http.get<IPost>(
            `${environment.API_URL}/api/Post/${id}`,
            this.getAuthOptions(token)
        );
    }

    createPost(post: IPostUpsert): Observable<IPost> {
        const token = this.authService.getUserToken();
        if (!this.hasValidToken(token)) {
            return of();
        }

        const payload = this.buildCreateOrUpdatePayload(post);
        return this.http.post<IPost>(
            `${environment.API_URL}/api/Post/create`,
            payload,
            this.getFormDataAuthOptions(token)
        );
    }

    updatePost(post: IPostUpsert): Observable<IPost> {
        const token = this.authService.getUserToken();
        if (!this.hasValidToken(token) || !post.id) {
            return of();
        }

        const payload = this.buildCreateOrUpdatePayload(post);
        payload.append('id', post.id);
        payload.append('currentImageFile', post.currentImageFile ?? '');

        return this.http.post<IPost>(
            `${environment.API_URL}/api/Post/update`,
            payload,
            this.getFormDataAuthOptions(token)
        );
    }

    deletePost(id: string): Observable<void> {
        const token = this.authService.getUserToken();
        if (!this.hasValidToken(token) || !id) {
            return of();
        }

        return this.http.post<void>(
            `${environment.API_URL}/api/Post/delete/${id}`,
            {},
            this.getAuthOptions(token)
        );
    }

    private buildCreateOrUpdatePayload(post: IPostUpsert): FormData {
        const payload = new FormData();
        payload.append('content', post.content ?? '');
        const communityIds = this.getCommunityIds(post);
        const primaryCommunityId = communityIds[0] ?? post.groupId ?? '';
        const videoType = this.normalizeVideoType(post.videoType);
        payload.append('groupId', primaryCommunityId);
        payload.append('groupIds', JSON.stringify(communityIds));
        payload.append('communityIds', JSON.stringify(communityIds));
        communityIds.forEach((id) => payload.append('groupIds[]', id));
        communityIds.forEach((id) => payload.append('communityIds[]', id));
        payload.append('publicPost', 'false');
        payload.append('publishOnNostr', 'false');
        payload.append('videoType', String(videoType));
        payload.append('videoLink', post.videoLink ?? '');

        if (post.imageFile) {
            payload.append('imageFile', post.imageFile);
        }

        return payload;
    }

    private getCommunityIds(post: IPostUpsert): string[] {
        const ids = post.communityIds ?? post.groupIds ?? (post.groupId ? [post.groupId] : []);
        return Array.from(new Set((ids ?? []).map((id) => `${id ?? ''}`.trim()).filter((id) => !!id)));
    }

    private normalizeVideoType(value: PostVideoType | number | null | undefined): PostVideoType {
        const numeric = typeof value === 'number' ? value : Number(value ?? PostVideoType.None);
        if (!Number.isFinite(numeric)) {
            return PostVideoType.None;
        }

        if (numeric === PostVideoType.YouTube || numeric === PostVideoType.Vimeo) {
            return numeric;
        }

        return PostVideoType.None;
    }

    private getAuthOptions(token: string): { headers: HttpHeaders } {
        return {
            headers: new HttpHeaders({
                'Content-type': 'application/json',
                Authorization: `Bearer ${token}`
            })
        };
    }

    private getFormDataAuthOptions(token: string): { headers: HttpHeaders } {
        return {
            headers: new HttpHeaders({
                Authorization: `Bearer ${token}`
            })
        };
    }

    private hasValidToken(token: string | null): token is string {
        return token !== undefined && token !== null && token !== '';
    }
}
